import React, { useState } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import useSelectedCompany from '../../hooks/useSelectedCompany';
import useCompanyOptions from '../../hooks/useCompanyOptions';
import LicensesHeader from './LicensesHeader';
import { useGetLicensesQuery, useGetPackagesQuery, useAddPackageMutation } from '../../services/api';
import LicenseTable from './LicensesTable';
import AddPackageDialogBox from './AddPackageDialogBox';
import { differenceInDays } from 'date-fns';

const Licenses = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // Company options
    const { companyOptions } = useCompanyOptions();
    const { selectedCompany, selectedCompanyId, handleCompanyChange, getSelectedCompanyLabel } = useSelectedCompany({ companyOptions });

    // API
    const { data: licenseData = [], error: licenseError, refetch: refetchLicensesData } = useGetLicensesQuery(selectedCompanyId, { skip: !selectedCompanyId });
    const processedLicenseData = licenseError || !licenseData ? [] : licenseData;
    const { data: packagesData, refetch: refetchPackageData } = useGetPackagesQuery();
    const [addPackage] = useAddPackageMutation();

    const [packageData, setPackageData] = useState({ name: '', description: '', durationInDays: '' });
    const [openPackageDialog, setOpenPackageDialog] = useState(false);
    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
        },
    ]);

    const handleOpenPackageDialog = () => setOpenPackageDialog(true);
    const handleClosePackageDialog = () => setOpenPackageDialog(false);

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPackageData((prevData) => ({ ...prevData, [name]: value }));
    };

    // Handle add package
    const handleAddPackage = async () => {
        const start = dateRange[0].startDate;
        const end = dateRange[0].endDate;
        const days = differenceInDays(end, start) + 1;

        try {
            await addPackage({
                ...packageData,
                durationInDays: days,
                startDate: start,
                endDate: end,
            }).unwrap();

            setOpenPackageDialog(false);
            refetchLicensesData();
            refetchPackageData();
        } catch (error) {
            console.error('Error adding package:', error);
        }
    };

    return (
        <Box m={isSmallScreen ? '1rem' : '1rem 2.5rem'}>
            {/* Header */}
            <LicensesHeader
                selectedCompany={selectedCompany}
                handleCompanyChange={handleCompanyChange}
                companyOptions={companyOptions}
                handleOpenPackageDialog={handleOpenPackageDialog}
                getSelectedCompanyLabel={getSelectedCompanyLabel()}
            />

            {/* Lisence table */}
            <LicenseTable licencesData={processedLicenseData} packagesData={packagesData} refetchLicensesData={refetchLicensesData} isSmallScreen />

            {/* Add package dialog box */}
            <AddPackageDialogBox
                open={openPackageDialog}
                handleCloseDialog={handleClosePackageDialog}
                packageData={packageData}
                handleInputChange={handleInputChange}
                handleAddPackage={handleAddPackage}
                dateRange={dateRange}
                setDateRange={setDateRange}
                refetchLicensesData={refetchLicensesData}
                refetchPackageData={refetchPackageData}
            />
        </Box>
    );
};

export default Licenses;
